import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../../redux/actions/loadingActions";
import { doc, setDoc, collection, getDocs, deleteDoc, Timestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap/dist/js/bootstrap.js";
import "./css/GaleriaFotos.css";
import { v4 as uuidv4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faXmark } from "@fortawesome/free-solid-svg-icons";
import { db } from "../../firebase";
import { mostrarError, mostrarExito, mostrarInfo } from "../../utils/Alertas";
import Modal from "../secondary/Modal";
import { Typography, TextField, Button } from "@mui/material";

interface PhotoMetadata {
    url: string;
    text1: string;
    text2: string;
    createdAt: Date;
}

interface PhotoDocument extends PhotoMetadata {
    id: string;
}

const GaleriaFotos: React.FC = () => {
    const dispatch = useDispatch();
    const storage = getStorage();
    const [photos, setPhotos] = useState<PhotoDocument[]>([]);
    const [file, setFile] = useState<File | null>(null);
    const [text1, setText1] = useState("");
    const [text2, setText2] = useState("");
    const [flipped, setFlipped] = useState<string | null>(null);
    const [photoToDelete, setPhotoToDelete] = useState<PhotoDocument>();
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);
    const [isBoxOpen, setIsBoxOpen] = useState<boolean>(false);
    const [isDeleteMode, setIsDeleteMode] = useState(false);

    const fetchPhotosWithMetadata = useCallback(async (): Promise<
        PhotoDocument[]
    > => {
        try {
            const querySnapshot = await getDocs(collection(db, "photos"));
            const photos: PhotoDocument[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                photos.push({
                    id: doc.id,
                    url: data.url,
                    text1: data.text1,
                    text2: data.text2,
                    createdAt: data.createdAt.toDate(),
                });
            });
            photos.sort(
                (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
            );
            return photos;
        } catch (error) {
            console.error("Error obteniendo las fotos:", error);
            mostrarError("Error obteniendo las fotos.");
            return [];
        }
    }, []);

    const uploadPhotoWithMetadata = async (
        file: File,
        text1: string,
        text2: string
    ): Promise<void> => {
        try {
            const storageRef = ref(storage, `photos/${uuidv4()}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            const docRef = doc(db, "photos", uuidv4());
            await setDoc(docRef, {
                url: downloadURL,
                text1,
                text2,
                createdAt: Timestamp.now(),
            });

            setFile(null);
            setText1("");
            setText2("");
            setIsModalUploadOpen(false);
            mostrarExito("Foto subida correctamente.");
        } catch (error) {
            console.error("Error subiendo la foto:", error);
            mostrarError("Error subiendo la foto.");
        }
    };

    const deletePhotoWithMetadata = async (): Promise<void> => {
        try {
            if (photoToDelete) {
                const storageRef = ref(storage, photoToDelete.url);
                await deleteObject(storageRef);

                const docRef = doc(db, "photos", photoToDelete.id);
                await deleteDoc(docRef);

                setPhotoToDelete(undefined);
                setIsConfirmModalOpen(false);
                mostrarExito("Foto eliminada correctamente.");
            }
        } catch (error) {
            console.error("Error eliminando foto:", error);
            mostrarError("Error eliminando foto.");
        }
    };

    const handleUpload = async () => {
        if (file && text1 && text2) {
            dispatch(showLoading());
            try {
                await uploadPhotoWithMetadata(file, text1, text2);
                const fetchedPhotos = await fetchPhotosWithMetadata();
                setPhotos(fetchedPhotos);
            } catch (error) {
                console.error("Error subiendo foto:", error);
                mostrarError("Error subiendo foto.");
            }
            dispatch(hideLoading());
        } else {
            mostrarInfo('Por favor, informe todos los campos del formulario.');
        }
    };

    const handleDelete = async (
        event: React.MouseEvent<SVGSVGElement>,
        photo: PhotoDocument
    ) => {
        event.stopPropagation();
        setPhotoToDelete(photo);
        setIsConfirmModalOpen(true);
    };

    const confirmDeletePhoto = async () => {
        if (!photoToDelete) return;
        dispatch(showLoading());
        await deletePhotoWithMetadata();
        const fetchedPhotos = await fetchPhotosWithMetadata();
        setPhotos(fetchedPhotos);
        dispatch(hideLoading());
    };

    const handleClick = (index: string) => {
        setFlipped(flipped === index ? null : index);
    };

    useEffect(() => {
        (async () => {
            dispatch(showLoading());
            const fetchedPhotos = await fetchPhotosWithMetadata();
            setPhotos(fetchedPhotos);
            dispatch(hideLoading());
        })();
    }, [dispatch, fetchPhotosWithMetadata]);

    return (
        <Fragment>
            <div className="galeria">
                {photos.map((photo) => (
                    <div key={photo.id} className="foto-item">
                        <div className={`foto-container ${flipped === photo.id ? "flipped" : ""}`} onClick={() => handleClick(photo.id)}>
                            <img src={photo.url} alt={`Foto ${photo.id}`} className="foto" />
                            <div className="foto-back">
                                <p>{photo.text1}</p>
                                <p style={{ fontWeight: "bold" }}>
                                    {photo.text2}
                                </p>
                            </div>
                            {isDeleteMode && (
                                <FontAwesomeIcon
                                    className="delete-button-galeria"
                                    onClick={(e) => handleDelete(e, photo)}
                                    icon={faXmark}
                                />
                            )}
                        </div>
                    </div>
                ))}

                <div className={`botones-galeria ${isBoxOpen ? "open" : "closed"}`}>
                    <FontAwesomeIcon
                        className="toggle-button"
                        icon={isBoxOpen ? faChevronDown : faChevronUp}
                        onClick={() => setIsBoxOpen(!isBoxOpen)}
                    />
                    <button className="main-button" onClick={() => setIsModalUploadOpen(true)}>Subir Foto</button>
                    {isDeleteMode ? (
                        <button
                            className="delete-primary-button"
                            onClick={() => setIsDeleteMode(false)}
                        >
                            Cancelar
                        </button>
                    ) : (
                        <button
                            className="delete-primary-button"
                            onClick={() => setIsDeleteMode(true)}
                        >
                            Eliminar Foto
                        </button>
                    )}
                </div>
            </div>

            {isConfirmModalOpen && (
                <Modal onClose={() => setIsConfirmModalOpen(false)}>
                    <div className="modal-body">
                        <h2>Confirmar Eliminación</h2>
                        <p>¿Estás seguro de que deseas eliminar esta foto? </p>
                        <p>Esta acción no se puede deshacer.</p>

                        <div className="row">
                            <div className="col-6" style={{ paddingLeft: 0 }}>
                                <button className="btn btn-secondary btn-block" onClick={() => setIsConfirmModalOpen(false)}>
                                    Cancelar
                                </button>
                            </div>
                            <div className="col-6" style={{ paddingRight: 0 }}>
                                <button className="btn btn-danger btn-block" onClick={confirmDeletePhoto}>
                                    Eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {isModalUploadOpen && (
                <Modal onClose={() => setIsModalUploadOpen(false)}>
                    <div className="modal-body">
                        <h2>Subir una foto</h2>
                        <form onSubmit={(e) => { e.preventDefault(); handleUpload(); }}>
                            <Button variant="outlined" component="label" fullWidth sx={{ marginBottom: 1 }}>
                                Subir imagen
                                <input
                                    type="file"
                                    hidden
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                />
                            </Button>

                            {file && (
                                <Typography variant="body2" color="textSecondary" className="galeria-archivo-seleccionado" sx={{ mb: 0 }}>
                                    Archivo seleccionado: {file.name}
                                </Typography>
                            )}

                            <TextField
                                type="text"
                                label="Fecha"
                                variant="outlined"
                                value={text1}
                                onChange={(e) => setText1(e.target.value)}
                                fullWidth
                                margin="normal"
                            />

                            <TextField
                                type="text"
                                label="Ciudad"
                                variant="outlined"
                                value={text2}
                                onChange={(e) => setText2(e.target.value)}
                                fullWidth
                                margin="normal"
                                style={{ marginTop: "10px" }}
                            />
                            <button className="main-button" type="submit">Subir Foto</button>
                        </form>
                    </div>
                </Modal>
            )}
        </Fragment>
    );
};

export default GaleriaFotos;
